import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  Upload,
  FileText,
  Shield,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronDown,
  ChevronUp,
  ArrowRight,
  Scale,
  X,
  Loader2,
  Eye,
  Lock
} from 'lucide-react';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const ResponseDetection = ({ conversationId, isPaid, onDetectionComplete }) => {
  const [originalFile, setOriginalFile] = useState(null);
  const [responseFile, setResponseFile] = useState(null);
  const [questionAsked, setQuestionAsked] = useState('');
  const [remedyStage, setRemedyStage] = useState('');
  const [respondingParty, setRespondingParty] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [showContext, setShowContext] = useState(false);
  const [error, setError] = useState('');
  const originalRef = useRef(null);
  const responseRef = useRef(null);

  const handleAnalyze = async () => {
    if (!originalFile || !responseFile) return;
    setAnalyzing(true);
    setError('');
    setResult(null);

    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('original_file', originalFile);
      formData.append('response_file', responseFile);
      if (questionAsked) formData.append('question_asked', questionAsked);
      if (remedyStage) formData.append('remedy_stage', remedyStage);
      if (respondingParty) formData.append('responding_party', respondingParty);
      if (conversationId) formData.append('conversation_id', conversationId);

      const res = await axios.post(`${API_BASE}/api/response-detection/analyze`, formData, {
        headers: { 'Authorization': `Bearer ${token}` },
        timeout: 120000,
      });

      if (res.data.success) {
        setResult(res.data);
        // Auto-trigger Strategy Brain refresh after detection completes
        if (onDetectionComplete) {
          onDetectionComplete();
        }
      } else {
        setError('Analysis failed. Please try again.');
      }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const getClassificationColor = (classification) => {
    const map = {
      substantive: 'bg-green-100 text-green-800 border-green-300',
      partial: 'bg-yellow-100 text-yellow-800 border-yellow-300',
      evasive: 'bg-orange-100 text-orange-800 border-orange-300',
      boilerplate: 'bg-orange-100 text-orange-800 border-orange-300',
      dishonored: 'bg-red-100 text-red-800 border-red-300',
      non_responsive: 'bg-red-100 text-red-800 border-red-300',
    };
    return map[classification] || 'bg-gray-100 text-gray-800';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'fully_addressed': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'partially_addressed': return <AlertTriangle className="w-4 h-4 text-yellow-600" />;
      case 'ignored': return <XCircle className="w-4 h-4 text-red-600" />;
      case 'redirected': return <ArrowRight className="w-4 h-4 text-orange-600" />;
      case 'denied_without_evidence': return <XCircle className="w-4 h-4 text-red-600" />;
      default: return <AlertTriangle className="w-4 h-4 text-gray-500" />;
    }
  };

  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-700';
    if (score >= 40) return 'text-yellow-700';
    return 'text-red-700';
  };

  const getProgressColor = (score, inverse = false) => {
    const effective = inverse ? 100 - score : score;
    if (effective >= 70) return '[&>div]:bg-green-500';
    if (effective >= 40) return '[&>div]:bg-yellow-500';
    return '[&>div]:bg-red-500';
  };

  const handleReset = () => {
    setOriginalFile(null);
    setResponseFile(null);
    setQuestionAsked('');
    setRemedyStage('');
    setRespondingParty('');
    setResult(null);
    setError('');
    setShowContext(false);
  };

  const analysis = result?.analysis;

  return (
    <div className="max-w-5xl mx-auto p-6 space-y-6" data-testid="response-detection-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-3">
            <Shield className="w-7 h-7 text-blue-700" />
            Smart Response Detection
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            AI-powered analysis that compares your original notice against the response you received.
          </p>
        </div>
        {result && (
          <Button variant="outline" onClick={handleReset} data-testid="reset-btn">
            New Analysis
          </Button>
        )}
      </div>

      {/* Detailed Description */}
      {!result && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 space-y-4" data-testid="detection-description">
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2">What is Smart Response Detection?</h3>
            <p className="text-sm text-gray-700 leading-relaxed">
              When you send a Conditional Acceptance or Notice of Default via certified mail, the opposing party may respond — but not all responses are created equal. Many responses use <strong>boilerplate language</strong>, <strong>evasive deflections</strong>, or simply <strong>ignore</strong> the specific demands you raised. The Smart Response Detection system uses AI to perform a <strong>point-by-point comparison</strong> between your original document and the response you received, exposing exactly where the opposing party failed to address your claims.
            </p>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2">What It Detects</h3>
            <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside">
              <li><strong>Non-Responses</strong> — Demands that were completely ignored or unanswered</li>
              <li><strong>Evasive Deflections</strong> — Generic replies that redirect without providing proof</li>
              <li><strong>Boilerplate Language</strong> — Pre-written corporate templates with no case-specific substance</li>
              <li><strong>Partial Responses</strong> — Claims acknowledged but not fully addressed with verified evidence</li>
              <li><strong>Dishonored Replies</strong> — Responses that fail to meet the terms of your Conditional Acceptance</li>
            </ul>
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 mb-2">How to Use It</h3>
            <ol className="text-sm text-gray-700 space-y-1 list-decimal list-inside">
              <li><strong>Upload your original document</strong> — The notice, Conditional Acceptance, or Opportunity to Cure you sent</li>
              <li><strong>Upload the response you received</strong> — The letter, email, or document they sent back (supports PDF, DOCX, TXT, and scanned images)</li>
              <li><strong>Add context</strong> (optional) — Specify which remedy stage you are in, who responded, and what question you need answered</li>
              <li><strong>Run the analysis</strong> — The AI will score the response on answer quality, completeness, and deflection risk, and give you a clear verdict on whether the response honors or dishonors your notice</li>
            </ol>
          </div>
          <p className="text-xs text-gray-500 italic">
            Results are automatically fed into the Strategy Brain and Battlefield Map to keep your case strategy up to date.
          </p>
        </div>
      )}

      {/* Upload Section */}
      {!result && !isPaid && (
        <Card className="border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="upgrade-gate">
          <CardContent className="p-10 text-center space-y-4">
            <Lock className="w-12 h-12 mx-auto text-amber-600" />
            <h2 className="text-xl font-bold text-gray-900">Paid Feature</h2>
            <p className="text-sm text-gray-700 max-w-md mx-auto">
              Smart Response Detection is available exclusively for paid subscribers. Upgrade your plan to compare notices against responses and detect evasions, deflections, and non-responses with AI analysis.
            </p>
            <Button
              className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white px-8"
              onClick={() => { window.location.href = '/dashboard/subscription'; }}
              data-testid="upgrade-plan-btn"
            >
              Upgrade Plan
            </Button>
          </CardContent>
        </Card>
      )}

      {!result && isPaid && (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Original Document */}
            <Card className={`border-2 border-dashed transition-colors ${originalFile ? 'border-green-400 bg-green-50' : 'border-gray-300 hover:border-blue-400'}`}>
              <CardContent className="p-6 text-center">
                <input
                  type="file"
                  ref={originalRef}
                  className="hidden"
                  accept=".pdf,.docx,.doc,.txt,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => { if (e.target.files?.[0]) setOriginalFile(e.target.files[0]); }}
                />
                {originalFile ? (
                  <div className="space-y-2">
                    <CheckCircle className="w-10 h-10 mx-auto text-green-600" />
                    <p className="font-semibold text-green-800">Original Document</p>
                    <p className="text-sm text-green-700 truncate">{originalFile.name}</p>
                    <p className="text-xs text-green-600">{(originalFile.size / 1024).toFixed(1)} KB</p>
                    <Button variant="outline" size="sm" onClick={() => { setOriginalFile(null); originalRef.current.value = ''; }}>
                      <X className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 cursor-pointer" onClick={() => originalRef.current?.click()}>
                    <Upload className="w-10 h-10 mx-auto text-gray-400" />
                    <p className="font-semibold text-gray-700">Your Original Notice / Demand</p>
                    <p className="text-xs text-gray-500">Upload the document you sent out</p>
                    <p className="text-xs text-gray-400">PDF, DOC, DOCX, TXT, or Image</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Response Document */}
            <Card className={`border-2 border-dashed transition-colors ${responseFile ? 'border-blue-400 bg-blue-50' : 'border-gray-300 hover:border-blue-400'}`}>
              <CardContent className="p-6 text-center">
                <input
                  type="file"
                  ref={responseRef}
                  className="hidden"
                  accept=".pdf,.docx,.doc,.txt,.jpg,.jpeg,.png,.gif,.webp"
                  onChange={(e) => { if (e.target.files?.[0]) setResponseFile(e.target.files[0]); }}
                />
                {responseFile ? (
                  <div className="space-y-2">
                    <CheckCircle className="w-10 h-10 mx-auto text-blue-600" />
                    <p className="font-semibold text-blue-800">Received Response</p>
                    <p className="text-sm text-blue-700 truncate">{responseFile.name}</p>
                    <p className="text-xs text-blue-600">{(responseFile.size / 1024).toFixed(1)} KB</p>
                    <Button variant="outline" size="sm" onClick={() => { setResponseFile(null); responseRef.current.value = ''; }}>
                      <X className="w-3 h-3 mr-1" /> Remove
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-3 cursor-pointer" onClick={() => responseRef.current?.click()}>
                    <FileText className="w-10 h-10 mx-auto text-gray-400" />
                    <p className="font-semibold text-gray-700">Response You Received</p>
                    <p className="text-xs text-gray-500">Upload the reply from the opposing party</p>
                    <p className="text-xs text-gray-400">PDF, DOC, DOCX, TXT, or Image</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Optional Context */}
          <div>
            <button
              onClick={() => setShowContext(!showContext)}
              className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
              data-testid="toggle-context-btn"
            >
              {showContext ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              Optional Context (improves accuracy)
            </button>
            {showContext && (
              <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">What were you asking?</label>
                  <Input
                    placeholder="e.g., Proof of claim, verified accounting..."
                    value={questionAsked}
                    onChange={(e) => setQuestionAsked(e.target.value)}
                    data-testid="question-asked-input"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Remedy Stage</label>
                  <Select value={remedyStage} onValueChange={setRemedyStage}>
                    <SelectTrigger data-testid="remedy-stage-select">
                      <SelectValue placeholder="Select stage..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="initial_notice">Initial Notice</SelectItem>
                      <SelectItem value="conditional_acceptance">Conditional Acceptance</SelectItem>
                      <SelectItem value="opportunity_to_cure">Opportunity to Cure</SelectItem>
                      <SelectItem value="notice_of_fault">Notice of Fault</SelectItem>
                      <SelectItem value="notice_of_default">Notice of Default</SelectItem>
                      <SelectItem value="affidavit">Affidavit of Truth</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">Responding Party</label>
                  <Input
                    placeholder="e.g., ABC Bank, County Court..."
                    value={respondingParty}
                    onChange={(e) => setRespondingParty(e.target.value)}
                    data-testid="responding-party-input"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Analyze Button */}
          <div className="flex justify-center">
            <Button
              size="lg"
              className="px-8"
              disabled={!originalFile || !responseFile || analyzing}
              onClick={handleAnalyze}
              data-testid="analyze-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Analyzing Documents...
                </>
              ) : (
                <>
                  <Eye className="w-5 h-5 mr-2" />
                  Detect Response Quality
                </>
              )}
            </Button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}
        </div>
      )}

      {/* Results Section */}
      {analysis && (
        <div className="space-y-5" data-testid="analysis-results">
          {/* Verdict Banner */}
          <Card className={`border-2 ${getClassificationColor(analysis.response_classification)}`}>
            <CardContent className="p-6">
              <div className="flex items-start gap-4">
                <div className="flex-shrink-0">
                  <Scale className="w-10 h-10" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <Badge className={`text-sm px-3 py-1 ${getClassificationColor(analysis.response_classification)}`}>
                      {(analysis.response_classification || '').replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>
                  <p className="font-semibold text-lg">{analysis.verdict}</p>
                  <p className="text-sm mt-2 opacity-80">{analysis.summary}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Scores */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { label: 'Answer Quality', key: 'answer_quality', inverse: false },
              { label: 'Completeness', key: 'completeness', inverse: false },
              { label: 'Deflection Risk', key: 'deflection_risk', inverse: true },
              { label: 'Non-Response', key: 'non_response_probability', inverse: true },
            ].map(({ label, key, inverse }) => {
              const score = analysis.scores?.[key] ?? 0;
              return (
                <Card key={key}>
                  <CardContent className="p-4 text-center">
                    <p className="text-xs text-gray-500 mb-1">{label}</p>
                    <p className={`text-2xl font-bold ${inverse ? getScoreColor(100 - score) : getScoreColor(score)}`}>{score}</p>
                    <Progress value={score} className={`h-2 mt-2 ${getProgressColor(score, inverse)}`} />
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Issue Breakdown */}
          {analysis.issues_extracted?.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Issue-by-Issue Breakdown</CardTitle>
                <CardDescription>Each demand or question from your original document and how the response handled it</CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                {analysis.issues_extracted.map((issue, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-gray-50 border">
                    {getStatusIcon(issue.status)}
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-sm font-semibold text-gray-900">{issue.issue}</span>
                        <Badge variant="outline" className="text-xs">
                          {(issue.status || '').replace(/_/g, ' ')}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600">{issue.explanation}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Deflection Flags */}
          {analysis.deflection_flags?.length > 0 && (
            <Card className="border-orange-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-orange-600" />
                  Deflection Flags Detected
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {analysis.deflection_flags.map((flag, idx) => (
                  <div key={idx} className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                    <p className="text-sm font-medium text-orange-900">{flag.flag}</p>
                    <p className="text-xs text-orange-700 mt-1 italic">"{flag.evidence}"</p>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Equity Analysis */}
          {analysis.equity_analysis && (
            <Card className="border-blue-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Scale className="w-5 h-5 text-blue-700" />
                  Equity and Common Law Analysis
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {analysis.equity_analysis.applicable_maxims?.length > 0 && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-2">Applicable Maxims of Equity</p>
                    <div className="flex flex-wrap gap-2">
                      {analysis.equity_analysis.applicable_maxims.map((m, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">{m}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                {analysis.equity_analysis.honor_dishonor_assessment && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Honor / Dishonor Assessment</p>
                    <p className="text-sm text-gray-800">{analysis.equity_analysis.honor_dishonor_assessment}</p>
                  </div>
                )}
                {analysis.equity_analysis.common_law_observations && (
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase mb-1">Common Law Observations</p>
                    <p className="text-sm text-gray-800">{analysis.equity_analysis.common_law_observations}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Recommended Next Steps */}
          {analysis.recommended_next_steps?.length > 0 && (
            <Card className="border-green-200">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <ArrowRight className="w-5 h-5 text-green-700" />
                  Recommended Next Steps
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {analysis.recommended_next_steps.map((step, idx) => (
                  <div key={idx} className="flex items-start gap-3 p-3 rounded-lg bg-green-50 border border-green-200">
                    <Badge className={step.priority === 'high' ? 'bg-red-600' : step.priority === 'medium' ? 'bg-yellow-600' : 'bg-gray-500'}>
                      {step.priority}
                    </Badge>
                    <div>
                      <p className="text-sm font-semibold text-gray-900">{step.action}</p>
                      <p className="text-xs text-gray-600 mt-1">{step.reason}</p>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* File Info */}
          <div className="text-center text-xs text-gray-400 space-y-1">
            <p>Original: {result.original_filename} ({result.original_text_length?.toLocaleString()} chars)</p>
            <p>Response: {result.response_filename} ({result.response_text_length?.toLocaleString()} chars)</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResponseDetection;
